import { signal, Signal, WritableSignal } from '@angular/core';

/**
 * A generic, type-safe event emitter that integrates with Angular signals.
 *
 * Usage:
 * ```ts
 * interface MyEvents {
 *   click: { x: number; y: number };
 *   resize: { width: number; height: number };
 * }
 *
 * const emitter = new TypedEventEmitter<MyEvents>();
 *
 * // Signal-based (reactive):
 * const lastClick = emitter.on('click'); // Signal<{ x, y } | undefined>
 *
 * // Callback-based (imperative):
 * const unsub = emitter.subscribe('click', (e) => console.log(e.x));
 * unsub(); // cleanup
 * ```
 */
export class TypedEventEmitter<EventMap extends Record<string, unknown>> {
  private _signals = new Map<keyof EventMap, WritableSignal<unknown>>();
  private _listeners = new Map<keyof EventMap, Set<(event: unknown) => void>>();

  /**
   * Returns a read-only signal holding the latest event payload for the given type.
   * The signal updates each time `emit()` is called for this event type.
   */
  on<K extends keyof EventMap>(type: K): Signal<EventMap[K] | undefined> {
    if (!this._signals.has(type)) {
      this._signals.set(type, signal<EventMap[K] | undefined>(undefined));
    }
    return this._signals.get(type)!.asReadonly() as Signal<EventMap[K] | undefined>;
  }

  /**
   * Emit an event, updating the signal and invoking all registered listeners.
   */
  emit<K extends keyof EventMap>(type: K, event: EventMap[K]): void {
    if (this._signals.has(type)) {
      this._signals.get(type)!.set(event);
    }
    const listeners = this._listeners.get(type);
    if (listeners) {
      for (const listener of [...listeners]) {
        listener(event);
      }
    }
  }

  /**
   * Register a listener for an event type. Returns an unsubscribe function.
   * Duplicate listeners (by reference) are ignored.
   */
  subscribe<K extends keyof EventMap>(type: K, listener: (event: EventMap[K]) => void): () => void {
    if (!this._listeners.has(type)) {
      this._listeners.set(type, new Set());
    }
    this._listeners.get(type)!.add(listener as (event: unknown) => void);
    return () => this.unsubscribe(type, listener);
  }

  /**
   * Check if a specific listener is registered for an event type.
   */
  hasListener<K extends keyof EventMap>(type: K, listener: (event: EventMap[K]) => void): boolean {
    return this._listeners.get(type)?.has(listener as (event: unknown) => void) ?? false;
  }

  /**
   * Remove a specific listener for an event type.
   */
  unsubscribe<K extends keyof EventMap>(type: K, listener: (event: EventMap[K]) => void): void {
    this._listeners.get(type)?.delete(listener as (event: unknown) => void);
  }

  /**
   * Remove all listeners for a specific type, or all types if no type is given.
   */
  removeAllListeners<K extends keyof EventMap>(type?: K): void {
    if (type) {
      this._listeners.delete(type);
    } else {
      this._listeners.clear();
      this._signals.clear();
    }
  }
}
